<script setup lang="ts">
import { inject, type Ref, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import rutHelpers from 'rut-helpers';
import { type Patient, patientsApi, type PDFPatient } from '@/api/patients';
import { type SerializedUser } from '@/api/users';
import {
  statusChipColor, statusChipLabel, statusChipTooltip, formatShortDate,
} from '@/utils/patient-status';
import GoBackButton from '@/components/go-back-button.vue';
import InfoField from '@/components/info-field.vue';
import SectionHeader from '@/components/section-header.vue';
import PatientDocuments from '@/components/patients/patient-documents.vue';
import NotesSection from '@/components/note/notes-section.vue';
import RequirementsTab from '@/components/requirements/requirements-tab.vue';
import PatientInventoryTab from '@/components/inventory/patient-inventory-tab.vue';
import PatientMedicationsTab from '@/components/medications/patient-medications-tab.vue';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ResponsiveTabs from '@/components/responsive-tabs.vue';
import { usePermissions } from '@/composables/usePermissions';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import PatientServicePricesList from './patient-service-prices-list.vue';
import PatientTasksList from './patient-tasks-list.vue';
import PatientCaregiverPreferences from './patient-caregiver-preferences.vue';
import PatientServiceAssignmentsTimeline from './patient-service-assignments-timeline.vue';
import PatientAccessLog from './patient-access-log.vue';

const currentUser = inject<Ref<SerializedUser>>('currentUser');
const isDownloadingPdf = ref(false);
const { t } = useI18n();

// Allow deep-linking a tab via ?tab= (e.g. the route-plan unassigned panel links to
// ?tab=caregiver_preferences). No client router, so read the query string directly.
const TAB_VALUES = [
  'general', 'medical', 'tasks', 'documents', 'prices', 'observations',
  'requirements', 'caregiver_preferences', 'service_category', 'inventory', 'medications',
  'access_log',
];

function initialTab(): string {
  const requested = new URLSearchParams(window.location.search).get('tab');

  return requested && TAB_VALUES.includes(requested) ? requested : 'general';
}

const tab = ref(initialTab());

const { hasPermission } = usePermissions();
const { trackPatientEvent } = useAnalytics();

interface Props {
  patient: Patient,
  // Whether the patient has medication orders (only for those who read them).
  medicationHistory?: boolean,
}

const props = defineProps<Props>();

const patientGroupChips = computed(() => {
  if (!currentUser?.value?.companyGroupsEnabled) return [];
  return props.patient.groups ?? [];
});

const patientGroupIds = computed(() => patientGroupChips.value.map((g) => g.id));

const serviceCategoriesEnabled = computed(() =>
  !!currentUser?.value?.companyPatientServiceCategoriesEnabled);

// Activos / Inactivos / Archivados currency (care-planning companies only).
const statusEnabled = computed(() => !!currentUser?.value?.companyCarePlanningEnabled);
const isArchived = computed(() => !!props.patient.discardedAt);
const statusChip = computed(() => {
  if (!statusEnabled.value || isArchived.value || !props.patient.status) return null;

  return {
    label: statusChipLabel(props.patient.status, t),
    color: statusChipColor(props.patient.status),
    tooltip: statusChipTooltip(props.patient.status, t),
  };
});
const archivedSummary = computed(() => {
  const parts = [t('patientStatus.archivedBanner', { date: formatShortDate(props.patient.discardedAt) })];
  if (props.patient.humanDiscardReason) parts.push(props.patient.humanDiscardReason);
  if (props.patient.discardedByName) {
    parts.push(t('patientStatus.archivedBy', { name: props.patient.discardedByName }));
  }

  return parts.join(' · ');
});
const isReactivating = ref(false);
const reactivateError = ref(false);
function reactivatePatient() {
  isReactivating.value = true;
  reactivateError.value = false;
  patientsApi.reactivate(props.patient.id)
    .then(() => { window.location.reload(); })
    .catch(() => {
      reactivateError.value = true;
      isReactivating.value = false;
    });
}

// The timeline tab mutates assignments in place; it emits the fresh current assignment
// so the banner chip updates without a reload. undefined = no mutation yet.
type CurrentAssignmentLike = { categoryName: string; subcategoryName: string | null } | null;
const currentAssignmentOverride = ref<CurrentAssignmentLike | undefined>(undefined);

function handleCurrentAssignmentChanged(assignment: CurrentAssignmentLike) {
  currentAssignmentOverride.value = assignment;
}

const serviceCategoryChip = computed(() => {
  if (!serviceCategoriesEnabled.value) return null;
  const assignment = currentAssignmentOverride.value === undefined ?
    props.patient.currentServiceAssignment : currentAssignmentOverride.value;
  if (!assignment) return null;

  return assignment.subcategoryName ?
    `${assignment.categoryName} • ${assignment.subcategoryName}` : assignment.categoryName;
});

type InfoFieldData = { label: string; value: string; icon: string; link?: string };

const EMPTY = '--';

function formatValue(raw: unknown, suffix?: string): string {
  if (raw === null || raw === undefined || raw === '') return EMPTY;

  return suffix ? `${raw} ${suffix}` : String(raw);
}

function mapsLink(address: string | null | undefined): string | undefined {
  if (!address) return undefined;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const headerInfoFields = computed((): InfoFieldData[] => {
  const p = props.patient;
  const usesIdCode = currentUser?.value?.companyUsesIdCode;

  const fields: InfoFieldData[] = [
    ...(usesIdCode ? [{ label: 'Código', value: formatValue(p.idCode), icon: 'mdi-identifier' }] : []),
    { label: 'RUT', value: p.dni ? rutHelpers.rutFormat(p.dni) : EMPTY, icon: 'mdi-card-account-details' },
    { label: 'Pasaporte', value: formatValue(p.passportNumber), icon: 'mdi-passport' },
    { label: 'Fecha de Nacimiento', value: formatValue(p.humanBirthdate), icon: 'mdi-calendar' },
    { label: 'Edad', value: formatValue(p.age, 'años'), icon: 'mdi-cake-variant' },
    { label: 'Sexo', value: formatValue(p.humanSex), icon: 'mdi-gender-male-female' },
    { label: 'Peso', value: formatValue(p.weight, 'kg'), icon: 'mdi-weight-kilogram' },
    { label: 'Dirección', value: formatValue(p.address), icon: 'mdi-map-marker', link: mapsLink(p.address) },
    { label: 'Fecha de Inicio', value: formatValue(p.humanStartsOn), icon: 'mdi-calendar-start' },
    { label: 'Fecha de Término', value: formatValue(p.humanEndsOn), icon: 'mdi-calendar-end' },
  ];

  return fields;
});

const hasMedicalInfo = computed(() =>
  !!(props.patient.medicalHistory || props.patient.surgicalHistory ||
     props.patient.technicalAids || props.patient.insurances || props.patient.comments),
);

function handleButtonClick(buttonName: string) {
  trackPatientEvent(AnalyticsEvents.patients.profileButtonClicked, {
    buttonName,
  }, props.patient.id);
}

const canInventory = computed(() => (
  !!currentUser?.value?.companyInventoryEnabled && hasPermission.value('inventory', 'index')
));

// Who saw the patient's clinical data (DS 41 art. 9).
const canAccessLog = computed(() => hasPermission.value('clinical_access_logs', 'index'));

// Medications reveal diagnoses: they also need the medical information permission.
// Once the company turns the module off, a patient's history stays, read-only.
const medicationsEnabled = computed(() => !!currentUser?.value?.companyMedicationsEnabled);
const canMedications = computed(() => (
  (medicationsEnabled.value || props.medicationHistory) && hasPermission.value('medications', 'index') &&
  hasPermission.value('patients', 'read_medical_information')
));

const tabItems = computed(() => {
  const planning = !!currentUser?.value?.companyCarePlanningEnabled;
  const canMedical = hasPermission.value('patients', 'read_medical_information');
  const canTasks = hasPermission.value('tasks', 'index');
  const canPrices = hasPermission.value('service_prices', 'index');
  const canRequirements = planning && hasPermission.value('requirements', 'index');
  return [
    { value: 'general', title: 'General', icon: 'mdi-account', show: true },
    { value: 'medical', title: 'Historial Médico', icon: 'mdi-medical-bag', show: canMedical },
    { value: 'tasks', title: 'Tareas', icon: 'mdi-clipboard-list', show: canTasks },
    { value: 'medications', title: 'Medicamentos', icon: 'mdi-pill', show: canMedications.value },
    { value: 'documents', title: 'Documentos', icon: 'mdi-folder-open', show: true },
    { value: 'prices', title: 'Precios', icon: 'mdi-currency-usd', show: canPrices },
    { value: 'observations', title: 'Comentarios', icon: 'mdi-comment-text', show: true },
    { value: 'requirements', title: 'Requerimientos', icon: 'mdi-calendar-check', show: canRequirements },
    { value: 'caregiver_preferences', title: 'Profesionales', icon: 'mdi-account-star', show: planning },
    {
      value: 'service_category', title: 'Categoría', icon: 'mdi-tag-heart',
      show: serviceCategoriesEnabled.value,
    },
    { value: 'inventory', title: 'Inventario', icon: 'mdi-package-variant-closed', show: canInventory.value },
    { value: 'access_log', title: 'Accesos', icon: 'mdi-shield-account', show: canAccessLog.value },
  ].filter((item) => item.show);
});

// Preserve the per-tab analytics that the old per-v-tab @click handlers fired.
watch(tab, (value) => {
  if (value === 'tasks') handleButtonClick('view_assigned_tasks');
  else if (value === 'prices') handleButtonClick('view_service_prices');
});

function downloadPatientPDF() {
  trackPatientEvent(AnalyticsEvents.patients.fileDownloaded, {
    format: 'pdf',
  }, props.patient.id);
  isDownloadingPdf.value = true;
  patientsApi.showAsPdf(props.patient.id).then((response: PDFPatient) => {
    const { headers: responseHeaders, data, data: { type } } = response;
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = responseHeaders['content-disposition'].split('"')[1];
    link.click();
  }).finally(() => {
    isDownloadingPdf.value = false;
  });
}
</script>
<template>
  <v-container
    class="d-flex flex-column patient-container"
  >
    <go-back-button path="/patients" />

    <!-- Header Card -->
    <v-card class="mb-4">
      <v-alert
        v-if="statusEnabled && isArchived"
        type="warning"
        variant="tonal"
        density="comfortable"
        class="ma-4 mb-0"
        icon="mdi-archive"
        data-testid="archived-banner"
      >
        <div class="d-flex align-center flex-wrap ga-3">
          <div>
            <div>{{ archivedSummary }}</div>
            <div
              v-if="props.patient.discardNote"
              class="text-caption text-medium-emphasis"
            >
              {{ props.patient.discardNote }}
            </div>
            <div
              v-if="reactivateError"
              class="text-caption text-error"
            >
              {{ t('patientForm.reactivateError') }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            v-if="hasPermission('patients', 'update')"
            color="primary"
            variant="flat"
            size="small"
            :loading="isReactivating"
            data-testid="reactivate-button"
            @click="reactivatePatient"
          >
            {{ t('patientUndiscardForm.reactivate') }}
          </v-btn>
        </div>
      </v-alert>
      <div class="patient-banner pa-5 d-flex align-center justify-space-between flex-wrap ga-4">
        <div class="d-flex align-center ga-4">
          <v-avatar
            class="patient-avatar"
            size="72"
          >
            <v-icon
              icon="mdi-account"
              color="primary"
              size="36"
            />
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold">
              {{ props.patient.firstName }} {{ props.patient.lastName }}
            </div>
            <div
              v-if="props.patient.nickname"
              class="text-body-2 text-medium-emphasis"
            >
              {{ props.patient.nickname }}
            </div>
            <div
              v-if="patientGroupChips.length > 0 || serviceCategoryChip || statusChip"
              class="d-flex align-center ga-1 mt-2 flex-wrap"
            >
              <v-chip
                v-if="statusChip"
                size="small"
                variant="tonal"
                :color="statusChip.color"
                data-testid="status-chip"
              >
                {{ statusChip.label }}
                <v-tooltip
                  v-if="statusChip.tooltip"
                  activator="parent"
                  location="top"
                  :text="statusChip.tooltip"
                />
              </v-chip>
              <v-chip
                v-if="serviceCategoryChip"
                size="small"
                variant="tonal"
                color="primary"
                prepend-icon="mdi-tag-heart"
              >
                {{ serviceCategoryChip }}
              </v-chip>
              <v-icon
                v-if="patientGroupChips.length > 0"
                icon="mdi-account-group"
                size="x-small"
                class="text-medium-emphasis"
              />
              <v-chip
                v-for="chip in patientGroupChips"
                :key="chip.id"
                size="small"
                variant="tonal"
              >
                {{ chip.name }}
              </v-chip>
            </div>
          </div>
        </div>
        <div class="patient-actions">
          <v-btn
            v-if="hasPermission('patients', 'update')"
            icon="mdi-pencil"
            color="primary"
            size="small"
            variant="outlined"
            :href="`/patients/${props.patient.id}/edit`"
            @click="handleButtonClick('edit')"
          >
            <v-icon icon="mdi-pencil" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Editar
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="hasPermission('patients', 'read_medical_information')"
            icon="mdi-file-pdf-box"
            color="primary"
            size="small"
            variant="outlined"
            :loading="isDownloadingPdf"
            @click="downloadPatientPDF"
          >
            <v-icon icon="mdi-file-pdf-box" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Ficha PDF
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="hasPermission('shifts', 'index')"
            icon="mdi-calendar"
            color="primary"
            size="small"
            variant="outlined"
            :href="`/shifts?patient_id=${props.patient.id}`"
            @click="handleButtonClick('view_calendar')"
          >
            <v-icon icon="mdi-calendar" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Calendario
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Tab Navigation -->
    <v-card>
      <responsive-tabs
        v-model="tab"
        :items="tabItems"
      />

      <v-window v-model="tab">
        <!-- General Info Tab -->
        <v-window-item value="general">
          <v-card-text class="pa-5">
            <section-header
              icon="mdi-account"
              title="Información Personal"
              class="mb-5"
            />
            <v-row>
              <v-col
                v-for="field in headerInfoFields"
                :key="field.label"
                cols="12"
                sm="6"
                md="4"
              >
                <info-field
                  :icon="field.icon"
                  :label="field.label"
                  :value="field.value"
                  :link="field.link"
                />
              </v-col>
            </v-row>

            <section-header
              icon="mdi-account-group"
              title="Apoderados"
              class="mb-5 mt-8"
            />
            <template v-if="props.patient.representativesAttributes?.length">
              <template
                v-for="(rep, index) in props.patient.representativesAttributes"
                :key="rep.id"
              >
                <v-divider
                  v-if="index > 0"
                  class="my-4"
                />
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                    md="3"
                  >
                    <info-field
                      icon="mdi-account"
                      label="Nombre"
                      :value="`${rep.firstName} ${rep.lastName}`"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="3"
                  >
                    <info-field
                      icon="mdi-phone"
                      label="Teléfono"
                      :value="rep.phoneNumber || '--'"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="3"
                  >
                    <info-field
                      icon="mdi-email"
                      label="Email"
                      :value="rep.email || '--'"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="3"
                  >
                    <info-field
                      icon="mdi-card-account-details"
                      label="RUT"
                      :value="rep.dni ? rutHelpers.rutFormat(rep.dni) : '--'"
                    />
                  </v-col>
                </v-row>
              </template>
            </template>
            <p
              v-else
              class="text-body-2 text-medium-emphasis"
            >
              No hay apoderados registrados
            </p>
          </v-card-text>
        </v-window-item>

        <!-- Medical History Tab -->
        <v-window-item value="medical">
          <v-card-actions class="flex-wrap ga-2 px-4 pt-4 pb-0">
            <v-btn
              v-if="hasPermission('shifts', 'index') && hasPermission('patients', 'read_medical_information')"
              prepend-icon="mdi-file-document"
              append-icon="mdi-open-in-new"
              color="primary"
              size="small"
              variant="outlined"
              :href="`/patients/${props.patient.id}/shifts`"
              @click="handleButtonClick('view_medical_records')"
            >
              Ficha Clínica
            </v-btn>
            <v-btn
              v-if="hasPermission('patients', 'read_medical_information')"
              prepend-icon="mdi-clipboard-pulse"
              append-icon="mdi-open-in-new"
              color="primary"
              size="small"
              variant="outlined"
              :href="`/patients/${props.patient.id}/vital_signs`"
              @click="handleButtonClick('view_vital_signs')"
            >
              Signos Vitales
            </v-btn>
            <v-spacer />
            <clinical-record-history
              item-type="Patient"
              :item-id="props.patient.id"
              :edited="props.patient.antecedentesEdited"
              label="Antecedentes del paciente"
            />
          </v-card-actions>

          <v-card-text v-if="hasMedicalInfo">
            <div class="mb-6">
              <section-header
                icon="mdi-heart-pulse"
                title="Antecedentes Médicos"
                class="mb-3"
              />
              <p class="text-body-2 preserve-whitespace">
                {{ props.patient.medicalHistory || '--' }}
              </p>
            </div>

            <div class="mb-6">
              <section-header
                icon="mdi-hospital-box"
                title="Antecedentes Quirúrgicos"
                class="mb-3"
              />
              <p class="text-body-2 preserve-whitespace">
                {{ props.patient.surgicalHistory || '--' }}
              </p>
            </div>

            <div class="mb-6">
              <section-header
                icon="mdi-wheelchair-accessibility"
                title="Ayudas Técnicas"
                class="mb-3"
              />
              <p class="text-body-2 preserve-whitespace">
                {{ props.patient.technicalAids || '--' }}
              </p>
            </div>

            <div class="mb-6">
              <section-header
                icon="mdi-shield-check"
                title="Seguros y Convenios"
                class="mb-3"
              />
              <p class="text-body-2 preserve-whitespace">
                {{ props.patient.insurances || '--' }}
              </p>
            </div>
          </v-card-text>

          <v-card-text
            v-else
            class="d-flex flex-column align-center justify-center py-12"
          >
            <v-icon
              icon="mdi-medical-bag"
              size="64"
              color="grey-lighten-1"
            />
            <p class="text-body-1 text-medium-emphasis mt-4">
              No hay información médica registrada
            </p>
            <v-btn
              v-if="hasPermission('patients', 'update')"
              variant="tonal"
              color="primary"
              size="small"
              prepend-icon="mdi-pencil"
              class="mt-3"
              :href="`/patients/${props.patient.id}/edit`"
            >
              Completar Información
            </v-btn>
          </v-card-text>

          <v-card-text
            v-if="hasMedicalInfo"
            class="pa-5 pt-0"
          >
            <section-header
              icon="mdi-text-box"
              title="Observaciones"
              class="mb-5"
            />
            <p class="text-body-2 preserve-whitespace">
              {{ props.patient.comments || '--' }}
            </p>
          </v-card-text>
        </v-window-item>

        <!-- Tasks Tab -->
        <v-window-item
          v-if="hasPermission('tasks', 'index')"
          value="tasks"
        >
          <v-card-text>
            <patient-tasks-list :patient-id="props.patient.id" />
          </v-card-text>
        </v-window-item>

        <!-- Documents Tab -->
        <v-window-item value="documents">
          <v-card-text>
            <patient-documents :patient="props.patient" />
          </v-card-text>
        </v-window-item>

        <!-- Prices Tab -->
        <v-window-item
          v-if="hasPermission('service_prices', 'index')"
          value="prices"
        >
          <v-card-text>
            <patient-service-prices-list :patient-id="props.patient.id" />
          </v-card-text>
        </v-window-item>

        <!-- Requirements Tab -->
        <v-window-item
          v-if="currentUser?.companyCarePlanningEnabled && hasPermission('requirements', 'index')"
          value="requirements"
        >
          <v-card-text>
            <requirements-tab
              :patient-id="props.patient.id"
              :patient-group-ids="patientGroupIds"
            />
          </v-card-text>
        </v-window-item>

        <!-- Caregiver Preferences Tab -->
        <v-window-item
          v-if="currentUser?.companyCarePlanningEnabled"
          value="caregiver_preferences"
        >
          <v-card-text>
            <patient-caregiver-preferences :patient-id="props.patient.id" />
          </v-card-text>
        </v-window-item>

        <!-- Service Category Tab -->
        <v-window-item
          v-if="serviceCategoriesEnabled"
          value="service_category"
        >
          <v-card-text>
            <patient-service-assignments-timeline
              :patient-id="props.patient.id"
              @current-changed="handleCurrentAssignmentChanged"
            />
          </v-card-text>
        </v-window-item>

        <!-- Medications Tab -->
        <v-window-item
          v-if="canMedications"
          value="medications"
        >
          <v-card-text>
            <patient-medications-tab
              :patient-id="props.patient.id"
              :archived="isArchived"
              :read-only="!medicationsEnabled"
            />
          </v-card-text>
        </v-window-item>

        <!-- Inventory Tab -->
        <v-window-item
          v-if="canInventory"
          value="inventory"
        >
          <v-card-text>
            <patient-inventory-tab
              :patient-id="props.patient.id"
              :archived="isArchived"
            />
          </v-card-text>
        </v-window-item>

        <!-- Access Log Tab -->
        <v-window-item
          v-if="canAccessLog"
          value="access_log"
        >
          <v-card-text>
            <patient-access-log :patient-id="props.patient.id" />
          </v-card-text>
        </v-window-item>

        <!-- Observations Tab -->
        <v-window-item value="observations">
          <v-card-text class="pa-5">
            <section-header
              icon="mdi-comment-text"
              title="Comentarios"
              class="mb-5"
            />
            <notes-section
              :noteable-id="props.patient.id"
              :noteable-type="'Patient'"
              :can-create="hasPermission('patients', 'update')"
            />
          </v-card-text>
        </v-window-item>
      </v-window>
    </v-card>
  </v-container>
</template>

<style scoped>
.patient-container {
  max-width: 1200px;
}

.patient-banner {
  background: rgb(var(--v-theme-surface));
}

.patient-avatar {
  background: rgba(var(--v-theme-primary), .12);
}

.patient-actions {
  display: flex;
  gap: 8px;
}

.preserve-whitespace {
  white-space: pre-wrap;
}
</style>
